import React, { useState } from 'react';
import { Table, Space, Empty } from 'antd';
import { appMessage as message } from '../../utils/appMessage';
import type { ColumnsType } from 'antd/es/table';
import { PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { AppModal } from '../common/AppModal';
import { AppButton } from '../common/AppButton';
import { fetchBuybackReceipt } from '../../api/api';
import { printBuybackReceipt } from '../../utils/buybackReceipt';
import { BuybackStatusTag } from './BuybackStatusTag';
import { BuybackPhotos } from './BuybackPhotos';
import { CardThumb } from './CardThumb';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { FONT_SIZE, FONT_WEIGHT, SPACING } from '../../constants/constants';
import type { BuybackDetail, BuybackItem } from '../../types/buyback';
import { CARD_GRADE_LABEL } from '../../types/buyback';
import { formatWon } from '../../utils/format';

const won = formatWon;

interface Props {
  open:    boolean;
  detail:  BuybackDetail | null;
  onClose: () => void;
}

/**
 * 매입 오더 상세(읽기 전용). 준비 완료(고객 동의 대기)·완료·불성립 오더를 확인한다.
 * 완료/불성립 오더는 개인정보가 삭제돼 있고, 완료 오더는 매입된 명세와 총액이 내역으로 남는다.
 */
export const BuybackDetailView: React.FC<Props> = ({ open, detail, onClose }) => {
  const t = useThemeTokens();

  const columns: ColumnsType<BuybackItem> = [
    { title: '상품명', dataIndex: 'productName', minWidth: 240,
      render: (v: string, r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
          <CardThumb imageUrl={r.imageUrl} alt={v} />
          <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>{v}</span>
        </div>
      ) },
    { title: '판매코드', dataIndex: 'sellerProductCode', width: 160,
      render: (v: string | null) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>{v ?? '-'}</span> },
    { title: '수량', dataIndex: 'quantity', width: 70, align: 'center',
      render: (v: number) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>{v}</span> },
    { title: '등급', dataIndex: 'grade', width: 70, align: 'center',
      render: (v: BuybackItem['grade']) => (
        <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>{v ? CARD_GRADE_LABEL[v] : 'S'}</span>
      ) },
    { title: '단가', dataIndex: 'unitPrice', width: 110, align: 'right',
      render: (v: number | null) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>{won(v)}</span> },
    { title: '금액', key: 'amount', width: 120, align: 'right',
      render: (_: unknown, r) => <span style={{ fontSize: FONT_SIZE.sm, color: t.textPrimary }}>{won((r.unitPrice ?? 0) * r.quantity)}</span> },
    { title: '매입 가능', dataIndex: 'buyable', width: 90, align: 'center',
      render: (v: boolean) => (
        <span style={{ fontSize: FONT_SIZE.xs, color: v ? t.success : t.textMuted }}>{v ? '가능' : '불가'}</span>
      ) },
  ];

  const pending = detail?.status === 'READY';
  const isCompleted = detail?.status === 'COMPLETED';
  const [printing, setPrinting] = useState(false);

  // 영수증 인쇄: 완료 오더의 영수증 데이터를 받아 새 창에서 인쇄한다(개인정보 없음).
  const handlePrint = async () => {
    if (!detail) return;
    setPrinting(true);
    try {
      const r = await fetchBuybackReceipt(detail.id);
      if (!printBuybackReceipt(r)) {
        message.warning('팝업이 차단되었습니다. 이 사이트의 팝업을 허용해 주세요.');
      }
    } catch {
      message.error('영수증을 불러오지 못했습니다.');
    } finally {
      setPrinting(false);
    }
  };

  return (
    <AppModal
      title={
        <Space>
          <span>매입 오더 상세</span>
          {detail?.managementNumber && (
            <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.regular, color: t.textMuted }}>
              {detail.managementNumber}
            </span>
          )}
          {detail && <BuybackStatusTag status={detail.status} />}
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={860}
      footer={
        <Space>
          {isCompleted && (
            <AppButton icon={<PrinterOutlined />} loading={printing} onClick={handlePrint}>
              영수증 인쇄
            </AppButton>
          )}
          <AppButton onClick={onClose}>닫기</AppButton>
        </Space>
      }
    >
      {detail && (
        <>
          <div style={{ marginBottom: SPACING.sm, fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
            고객{' '}
            <span style={{ color: t.textPrimary, fontWeight: FONT_WEIGHT.semibold }}>
              {detail.name ?? '개인정보 삭제됨'}
            </span>
            {detail.phone && (
              <>
                <span style={{ margin: `0 ${SPACING.xs}px`, color: t.border }}>|</span>
                {detail.phone}
              </>
            )}
          </div>

          {pending && (
            <div style={{ marginBottom: SPACING.sm, fontSize: FONT_SIZE.sm, color: t.accent }}>
              고객이 태블릿에서 판매할 카드를 선택하고 동의하기를 기다리는 중입니다.
            </div>
          )}

          {/* 실물 카드 사진(보기 전용) */}
          <BuybackPhotos buybackId={detail.id} readOnly />

          <Table
            rowKey={(r) => String(r.id)}
            columns={columns}
            dataSource={detail.items}
            pagination={false}
            size="small"
            bordered
            scroll={{ y: 340 }}
            locale={{ emptyText: <Empty description="명세가 없습니다" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: SPACING.md }}>
            <span style={{ fontSize: FONT_SIZE.sm, color: t.textMuted }}>
              {detail.completedAt ? `완료 ${dayjs(detail.completedAt).format('YYYY/MM/DD HH:mm')}` : ''}
            </span>
            {detail.totalAmount != null && (
              <div style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary }}>
                매입 총액{' '}
                <span style={{ fontSize: FONT_SIZE.lg, fontWeight: FONT_WEIGHT.bold, color: t.accent }}>{won(detail.totalAmount)}</span>
              </div>
            )}
          </div>
        </>
      )}
    </AppModal>
  );
};
